(() => {
/* Wild Gainz prize flow.
   Drives the page behind the QR code on the back of the bag: a welcome, then
   lab results or the free prize, then which store, then the claim form, then
   a thank-you for that store.

   Everything happens in place inside one section. Panels are shown and hidden
   rather than navigated to, so nothing here touches history or reloads.

   Codes are Klaviyo's job, not ours. The form fires a client API event
   carrying the channel; the flow behind it picks the code and refuses anyone
   who already had one. Nothing here knows what a code looks like.

   Sales channels are section blocks, so which panel follows which is decided
   in Liquid and carried on the buttons as data attributes. This file holds no
   channel names.

   Nothing is looked up or cached in connectedCallback, because that runs when
   the element's start tag is parsed and its children may not exist yet. The
   theme loads this deferred, so they usually do, but a component that quietly
   does nothing under the other load order is a trap. Listeners are delegated
   on the element itself and the DOM is queried at the point of use. */

if (customElements.get('wildgainz-prize-flow')) return;

const KLAVIYO_REVISION = '2026-07-15';
const CLAIM_KEY = 'wg-prize-claimed';

/* Providers that deliver a +tagged address to the base mailbox. Stripping the
   tag before the address reaches Klaviyo collapses you+1@, you+2@, you+3@ into
   a single profile, which is what lets the flow's once-per-person check do
   its job. Left alone for unknown domains: plus addressing is a convention,
   not a rule, and a domain that never supported it would bounce the rewrite. */
const PLUS_PROVIDERS = new Set([
  'gmail.com', 'googlemail.com', 'outlook.com', 'hotmail.com', 'live.com',
  'msn.com', 'icloud.com', 'me.com', 'mac.com', 'fastmail.com',
  'protonmail.com', 'proton.me', 'pm.me', 'yahoo.com', 'ymail.com',
]);

/* Gmail alone ignores dots in the local part, so only Gmail gets them
   stripped. Doing this to any other provider would rewrite a real address. */
const DOT_PROVIDERS = new Set(['gmail.com', 'googlemail.com']);

function normalizeEmail(raw) {
  const email = String(raw || '').trim().toLowerCase();
  const at = email.lastIndexOf('@');
  if (at < 1 || at === email.length - 1) return email;

  let local = email.slice(0, at);
  let domain = email.slice(at + 1);

  // googlemail.com is an alias of gmail.com, same mailbox either way.
  if (domain === 'googlemail.com') domain = 'gmail.com';

  if (PLUS_PROVIDERS.has(domain)) {
    const plus = local.indexOf('+');
    // plus > 0 only: an address starting with + has no base to fall back to.
    if (plus > 0) local = local.slice(0, plus);
  }
  if (DOT_PROVIDERS.has(domain)) local = local.replace(/\./g, '');

  // A rewrite that empties the local part is a rewrite that broke the
  // address, so keep what the person actually typed.
  if (!local) return email;
  return local + '@' + domain;
}

function readStore(key) {
  // Safari in private mode and browsers set to block site data both throw on
  // access rather than returning null, so every read and write is guarded.
  try {
    return window.localStorage.getItem(key);
  } catch (error) {
    return null;
  }
}

function writeStore(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch (error) {
    /* Nothing to do: the flag is telemetry, not a gate. */
  }
}

/* Wrap each word of the welcome heading in a span carrying its index, so the
   stylesheet can stagger them in. Walks text nodes only, so the <em> that
   carries the yellow highlight keeps its shape and its words land inside it.
   Without JavaScript there are no spans and the heading is simply shown. */
function splitWords(heading) {
  if (!heading || heading.dataset.split === 'done') return 0;
  let count = 0;
  const walker = document.createTreeWalker(heading, NodeFilter.SHOW_TEXT);
  const textNodes = [];
  while (walker.nextNode()) textNodes.push(walker.currentNode);
  textNodes.forEach((node) => {
    const parts = node.nodeValue.split(/(\s+)/);
    if (parts.length < 2 && !parts[0].trim()) return;
    const frag = document.createDocumentFragment();
    parts.forEach((part) => {
      if (!part) return;
      if (!part.trim()) {
        frag.appendChild(document.createTextNode(part));
        return;
      }
      const span = document.createElement('span');
      span.className = 'wg-welcome__word';
      span.style.setProperty('--i', String(count));
      span.textContent = part;
      frag.appendChild(span);
      count += 1;
    });
    node.parentNode.replaceChild(frag, node);
  });
  // The stylesheet reads the count on the panel (custom properties do not
  // travel upward), so it is set there as well as on the heading.
  heading.style.setProperty('--wg-words', String(count));
  const panel = heading.closest('[data-panel]');
  if (panel) panel.style.setProperty('--wg-words', String(count));
  heading.dataset.split = 'done';
  return count;
}

/* Scratch foil over the prize card on the reveal step.
   The prize sits in the DOM underneath the whole time, so a screen reader
   reads it and a page without script simply shows it: the canvas is only ever
   painted from here. Pointer input erases the foil; half gone and the rest
   fades so nobody has to scrub every pixel, and lifting a finger past a third
   finishes it too. Enter reveals it for keyboard users, and a visually hidden
   button does the same for screen readers. It is never a gate on the form.

   Watermark images from the section's blocks are printed faintly across the
   foil. They are loaded with CORS so drawing them does not taint the canvas,
   which would break the pixel count; if the count is ever refused anyway, a
   rough estimate from the strokes takes over so the reveal still happens. */
function themeColor(name, fallback) {
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || fallback;
}

class ScratchCoat {
  constructor(host, onReveal) {
    this.host = host;
    this.onReveal = onReveal;
    this.canvas = host.querySelector('canvas.wg-scratch');
    this.done = true;
    if (!this.canvas) return;
    this.card = this.canvas.parentElement;
    // Read back often (the progress count), so keep the bitmap in memory.
    this.ctx = this.canvas.getContext('2d', { willReadFrequently: true });
    this.pointerId = null;
    // Watermarks start loading now, well before anyone reaches the card.
    this.marks = Array.from(host.querySelectorAll('[data-scratch-image]'));
    this.marks.forEach((img) => { if (img.decode) img.decode().catch(() => {}); });
    this.canvas.addEventListener('pointerdown', (event) => this.start(event));
    this.canvas.addEventListener('pointermove', (event) => this.move(event));
    this.canvas.addEventListener('pointerup', (event) => { if (event.pointerId === this.pointerId) this.check(true); });
    this.canvas.addEventListener('pointercancel', (event) => { if (event.pointerId === this.pointerId) this.check(true); });
    host.addEventListener('keydown', (event) => {
      if (this.done) return;
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.reveal();
      }
    });
    const skip = host.querySelector('[data-prize-reveal]');
    if (skip) skip.addEventListener('click', () => this.reveal());
    // A turned phone gets a fresh coat rather than a stretched one. Only a
    // real change of card size counts: phones fire resize whenever the
    // browser toolbar slides away on scroll, and that must not wipe a half
    // scratched card.
    window.addEventListener('resize', () => {
      if (this.done || this.host.hidden) return;
      const rect = this.card.getBoundingClientRect();
      if (Math.abs(rect.width - (this.paintedW || 0)) < 1 && Math.abs(rect.height - (this.paintedH || 0)) < 1) return;
      this.paint();
    });
  }

  /* Lays a fresh foil over the card. Called every time the reveal is shown,
     which also handles a change of channel since the last visit. */
  paint() {
    if (!this.canvas) return;
    const rect = this.card.getBoundingClientRect();
    const w = rect.width;
    const h = rect.height;
    if (!w || !h) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    this.canvas.width = Math.round(w * dpr);
    this.canvas.height = Math.round(h * dpr);
    this.paintedW = w;
    this.paintedH = h;
    this.area = w * h;
    this.erased = 0;
    const ctx = this.ctx;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = 1;
    ctx.fillStyle = themeColor('--wg-color-cream', '#FAF5EC');
    ctx.fillRect(0, 0, w, h);
    // A faint diagonal hatch, so the foil reads as a surface, not a blank.
    ctx.strokeStyle = 'rgba(22, 24, 28, 0.08)';
    ctx.lineWidth = 1;
    for (let x = -h; x < w + h; x += 10) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x + h, h);
      ctx.stroke();
    }
    this.marksDrawn = this.watermark(ctx, w, h);
    // A dashed inner edge gives it the look of a ticket to be scratched.
    ctx.setLineDash([5, 5]);
    ctx.strokeStyle = 'rgba(22, 24, 28, 0.3)';
    ctx.lineWidth = 1.5;
    ctx.strokeRect(10, 10, w - 20, h - 20);
    ctx.setLineDash([]);
    // From here on, drawing erases.
    ctx.globalCompositeOperation = 'destination-out';

    this.done = false;
    this.drawing = false;
    this.moves = 0;
    this.canvas.classList.remove('is-done');
    this.host.classList.remove('is-revealed');
    this.toggle(true);
    document.documentElement.classList.add('wg-scratching');
  }

  /* The fruit, printed faintly and scattered. Positions come from a fixed
     seed so a repaint (new channel, turned phone) lays the same pattern. */
  watermark(ctx, w, h) {
    const ready = this.marks.filter((img) => img.complete && img.naturalWidth);
    if (!ready.length) return 0;
    let seed = 7;
    const rand = () => {
      seed = (seed * 1103515245 + 12345) % 2147483648;
      return seed / 2147483648;
    };
    // Every uploaded mark gets printed at least once.
    const count = Math.max(7, ready.length);
    let drawn = 0;
    for (let i = 0; i < count; i += 1) {
      const img = ready[i % ready.length];
      const size = 62 + rand() * 40;
      const x = 24 + rand() * (w - 48);
      const y = 20 + rand() * (h - 40);
      const angle = (rand() - 0.5) * 0.9;
      const ratio = img.naturalHeight / img.naturalWidth;
      ctx.save();
      ctx.globalAlpha = 0.22;
      ctx.translate(x, y);
      ctx.rotate(angle);
      try {
        ctx.drawImage(img, -size / 2, (-size * ratio) / 2, size, size * ratio);
        drawn += 1;
      } catch (error) {
        /* A broken image is left out; the foil is fine without it. */
      }
      ctx.restore();
    }
    ctx.globalAlpha = 1;
    return drawn;
  }

  /* Before the scratch: the scratch heading and its hint. After: the
     congratulations. The stamp itself is there throughout. */
  toggle(coated) {
    const before = this.host.querySelector('[data-prize-before]');
    const after = this.host.querySelector('[data-prize-after]');
    const skip = this.host.querySelector('[data-prize-reveal]');
    if (before) before.hidden = !coated;
    if (after) after.hidden = coated;
    if (skip) skip.hidden = !coated;
  }

  point(event) {
    const rect = this.canvas.getBoundingClientRect();
    return [event.clientX - rect.left, event.clientY - rect.top];
  }

  erase(x, y) {
    this.ctx.beginPath();
    this.ctx.arc(x, y, 26, 0, Math.PI * 2);
    this.ctx.fill();
    // Kept for the fallback estimate only; strokes overlap, so it overcounts.
    this.erased += Math.PI * 26 * 26;
  }

  start(event) {
    if (this.done) return;
    // One finger at a time, and for a mouse only the main button: a right
    // click opens a menu that swallows the matching pointerup, and a stroke
    // left open would then erase with no button held.
    if (event.isPrimary === false) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    this.pointerId = event.pointerId;
    // Capture keeps the stroke going when a fast finger leaves the canvas.
    // Synthetic events have no active pointer to capture, hence the guard.
    try { this.canvas.setPointerCapture(event.pointerId); } catch (error) { /* fine */ }
    this.drawing = true;
    const [x, y] = this.point(event);
    this.erase(x, y);
    this.last = [x, y];
  }

  move(event) {
    if (!this.drawing || this.done) return;
    if (event.pointerId !== this.pointerId) return;
    if (event.pointerType === 'mouse' && event.buttons === 0) {
      this.check(true);
      return;
    }
    const [x, y] = this.point(event);
    // Interpolate from the last point so a quick swipe leaves a line, not
    // a trail of dots.
    const [lx, ly] = this.last;
    const distance = Math.hypot(x - lx, y - ly);
    const steps = Math.max(1, Math.floor(distance / 8));
    for (let i = 1; i <= steps; i += 1) {
      this.erase(lx + ((x - lx) * i) / steps, ly + ((y - ly) * i) / steps);
    }
    this.last = [x, y];
    this.moves += 1;
    if (this.moves % 8 === 0) this.check(false);
  }

  /* Share of the foil already gone, sampled every seventh pixel. If the
     browser refuses the read (a watermark that tainted the canvas), the
     stroke estimate stands in, divided down for its overlaps. */
  progress() {
    const width = this.canvas.width;
    const height = this.canvas.height;
    if (!width || !height) return 0;
    try {
      const data = this.ctx.getImageData(0, 0, width, height).data;
      let clear = 0;
      let total = 0;
      for (let i = 3; i < data.length; i += 28) {
        total += 1;
        if (data[i] === 0) clear += 1;
      }
      return total ? clear / total : 0;
    } catch (error) {
      return this.area ? Math.min(1, this.erased / (this.area * 1.8)) : 0;
    }
  }

  /* Mid stroke the bar is half. On lifting the finger it drops to a third:
     someone who has clearly had a go should not be made to keep going. */
  check(final) {
    if (this.done) return;
    if (final) this.drawing = false;
    if (this.progress() >= (final ? 0.35 : 0.5)) this.reveal();
  }

  reveal() {
    if (this.done) return;
    this.done = true;
    this.drawing = false;
    this.canvas.classList.add('is-done');
    this.host.classList.add('is-revealed');
    this.toggle(false);
    document.documentElement.classList.remove('wg-scratching');
    if (this.onReveal) this.onReveal();
  }
}

class WildgainzPrizeFlow extends HTMLElement {
  connectedCallback() {
    // The theme editor can re-render the section around this element, so it
    // may reconnect. Binding must happen once.
    if (this.bound) return;
    this.bound = true;

    this.history = [];
    this.channel = '';
    // The markup already ships the welcome panel visible and the rest hidden,
    // so the starting step is recorded without touching the DOM.
    this.current = 'welcome';

    this.blocked = String(this.dataset.blockedDomains || '')
      .toLowerCase()
      .split(/[\s,]+/)
      .map((entry) => entry.replace(/^@+/, '').trim())
      .filter(Boolean);

    this.addEventListener('click', (event) => this.onClick(event));
    // submit bubbles, so one delegated listener covers the form wherever it
    // sits and however late it was parsed. submit() checks it is our form:
    // the review app's own form renders inside this element too.
    this.addEventListener('submit', (event) => this.submit(event));

    // A blocked domain leaves a custom validity error on the field, which
    // would bar every later submit until it is cleared. Editing clears it.
    // Both events, because some autofill paths fire only one of them.
    const clearEmailError = (event) => {
      const field = event.target;
      if (field && field.name === 'email' && field.form === this.formEl) field.setCustomValidity('');
    };
    this.addEventListener('input', clearEmailError);
    this.addEventListener('change', clearEmailError);

    // In the theme editor the later steps are hidden until a visitor walks
    // to them, which no merchant can do from the customizer. Selecting a
    // block in the sidebar shows the step that block belongs to, filled in
    // from that block, the way the theme's other components do.
    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:block:select', (event) => this.onEditorSelect(event));
    }

    // Children may not be parsed yet (see the header comment), so the split
    // waits for the document if it has to. Reduced motion is honoured in CSS,
    // where the split is harmless.
    // Ready is stamped in the same task as the split, so the stylesheet's
    // sequence starts the instant the words exist and never a frame before.
    const split = () => {
      splitWords(this.querySelector('[data-prize-split]'));
      const welcome = this.panel('welcome');
      if (welcome) welcome.classList.add('wg-welcome--ready');
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', split, { once: true });
    } else {
      split();
    }
  }

  get panelEls() {
    return Array.from(this.querySelectorAll('[data-panel]'));
  }

  get formEl() {
    return this.querySelector('[data-prize-form]');
  }

  get statusEl() {
    return this.querySelector('[data-prize-status]');
  }

  /* Takes a channel's data off its button. Every branching decision was
     made in Liquid from the block's mode, so nothing here knows or cares
     which sales channel this is. A channel added in the customizer works
     without a change to this file. */
  applyChoice(choice) {
    this.channel = choice.dataset.channelKey || '';
    this.channelLabel = choice.dataset.channelName || '';
    this.reward = choice.dataset.reward || '';
    this.reviewPanel = choice.dataset.reviewPanel || '';
    this.fill('[data-prize-reward]', this.reward);
    this.fill('[data-prize-badge]', choice.dataset.badge || '');
    this.fill('[data-prize-stamp]', this.reward ? ' ' : '');
    this.fill('[data-prize-fine]', choice.dataset.fine || '');
  }

  onEditorSelect(event) {
    if (!this.contains(event.target)) return;
    const id = event.detail && event.detail.blockId;
    const escape = (value) => (window.CSS && CSS.escape ? CSS.escape(value) : value);
    const panel = event.target.closest('[data-panel]');
    // A channel block: its thank-you, filled from its own button.
    let choice = id ? this.querySelector('[data-channel="' + escape(id) + '"]') : null;
    // Anything inside a thank-you (the review app block): that channel.
    if (!choice && panel && panel.dataset.panel.indexOf('ch-') === 0) {
      choice = this.querySelector('[data-channel="' + escape(panel.dataset.panel.slice(3)) + '"]');
    }
    if (choice) {
      this.applyChoice(choice);
      this.show(this.reviewPanel, { track: false, focus: false });
      return;
    }
    // A watermark: the reveal, coated with the first channel's prize.
    if (event.target.matches('[data-scratch-image]')) {
      const first = this.querySelector('[data-channel]');
      if (first) this.applyChoice(first);
      this.show('reveal', { track: false, focus: false });
      this.coat();
      return;
    }
    if (panel) this.show(panel.dataset.panel, { track: false, focus: false });
  }

  onClick(event) {
    // Nothing moves while a claim is in flight: a step change mid send
    // would land the answer on the wrong thank-you.
    if (this.busy) return;
    const choice = event.target.closest('[data-channel]');
    if (choice && this.contains(choice)) {
      this.applyChoice(choice);
      this.show(choice.dataset.goto || 'reveal');
      this.coat();
      return;
    }

    const go = event.target.closest('[data-prize-goto]');
    if (go && this.contains(go)) {
      this.show(go.dataset.prizeGoto);
      return;
    }

    const back = event.target.closest('[data-prize-back]');
    if (back && this.contains(back)) {
      event.preventDefault();
      this.back();
    }
  }

  panel(name) {
    return this.panelEls.find((panel) => panel.dataset.panel === name) || null;
  }

  show(name, options) {
    const opts = options || {};
    const panels = this.panelEls;
    const target = panels.find((panel) => panel.dataset.panel === name);
    if (!target) return;

    if (opts.track !== false && this.current && this.current !== name) {
      this.history.push(this.current);
    }

    // Once the visitor has left the welcome its entrance has done its job.
    // Marked so a later trip back does not replay the whole sequence.
    const leaving = panels.find((panel) => panel.dataset.panel === this.current);
    if (leaving && leaving.dataset.panel === 'welcome') leaving.classList.add('wg-welcome--played');

    this.current = name;
    if (name !== 'reveal') document.documentElement.classList.remove('wg-scratching');

    panels.forEach((panel) => {
      panel.hidden = panel !== target;
    });

    // A back link is only honest when there is somewhere to go back to.
    const back = target.querySelector('[data-prize-back]');
    if (back) back.hidden = this.history.length === 0;

    // Focus the panel rather than its first control: the panel carries the
    // heading, so a screen reader announces what this step is before what it
    // wants. Focusing also scrolls the new step into view on a long page.
    if (opts.focus !== false) target.focus({ preventScroll: false });
  }

  back() {
    const previous = this.history.pop();
    if (previous) this.show(previous, { track: false });
  }

  /* A fresh scratch coat over the reveal. With the setting off there is no
     canvas and the continue button is already showing, so this is a no-op.
     Painting happens on the next frame so the panel is laid out first. */
  coat() {
    const host = this.panel('reveal');
    if (!host) return;
    if (!this.scratch) {
      this.scratch = new ScratchCoat(host, () => {
        const next = host.querySelector('[data-prize-continue]');
        if (!next) return;
        next.hidden = false;
        const button = next.querySelector('[data-prize-goto]');
        if (button) button.focus();
      });
    }
    const next = host.querySelector('[data-prize-continue]');
    // Nothing to scratch (setting off, no canvas, or a channel with no
    // reward): the step is already revealed, button and all.
    if (this.dataset.scratch !== 'true' || !this.scratch.canvas || !this.reward) {
      this.scratch.done = true;
      this.scratch.toggle(false);
      if (next) next.hidden = false;
      return;
    }
    if (next) next.hidden = true;
    requestAnimationFrame(() => this.scratch.paint());
  }

  /* Writes text into every matching slot and hides the slots when there is
     none. The reward and its qualifier appear on more than one step. A slot
     that has children of its own (a reward stamp) only has its visibility
     toggled, so the text passed for it is just a yes or no. */
  fill(selector, text) {
    this.querySelectorAll(selector).forEach((el) => {
      if (!el.children.length) el.textContent = text;
      el.hidden = !text;
    });
  }

  endpoint(path) {
    return (
      'https://a.klaviyo.com/client/' +
      path +
      '/?company_id=' +
      encodeURIComponent(this.dataset.publicKey)
    );
  }

  post(path, payload) {
    return fetch(this.endpoint(path), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        revision: KLAVIYO_REVISION,
      },
      body: JSON.stringify(payload),
    });
  }

  async submit(event) {
    // Only our form. The review app's form also lives inside this element,
    // and its submits must reach the app untouched.
    const form = this.formEl;
    if (!form || event.target !== form) return;
    event.preventDefault();
    if (this.busy) return;

    const data = new FormData(form);

    if (data.get('wg_website')) {
      // Tripped honeypot. Almost certainly a bot, but a real person caught by
      // an overeager autofill must not be left at a dead button, so move them
      // along without sending anything.
      this.advance();
      return;
    }

    const typed = String(data.get('email') || '').trim();
    const name = String(data.get('name') || '').trim();
    if (!typed) return;

    const email = normalizeEmail(typed);
    const domain = email.slice(email.lastIndexOf('@') + 1);
    if (this.blocked.includes(domain)) {
      // Reported through the field's own validation bubble rather than the
      // status line, so the message sits next to the input that caused it.
      const emailInput = form.querySelector('[name="email"]');
      if (emailInput) {
        emailInput.setCustomValidity(this.dataset.blockedText || 'Use a different email address.');
        emailInput.reportValidity();
      }
      return;
    }

    // A busy flag rather than disabling the button: disabling the focused
    // control throws keyboard focus back to the document (WCAG 2.4.3).
    this.busy = true;
    this.say(this.dataset.sendingText);

    const repeat = readStore(CLAIM_KEY);
    const profileAttributes = { email: email, first_name: name };
    // Pinned before the wait, so the answer lands on the thank-you for the
    // channel that was claimed, whatever happens on screen meanwhile.
    const reviewPanel = this.reviewPanel;

    try {
      const response = await this.post('events', {
        data: {
          type: 'event',
          attributes: {
            properties: {
              // Flat, top-level properties: the Klaviyo flow splits on
              // `channel`, and split conditions cannot read nested data.
              channel: this.channel || 'unknown',
              channel_label: this.channelLabel || '',
              reward: this.reward || '',
              source_url: window.location.href,
              // Not a gate. Clearing site data resets it, so it is only ever
              // a signal that this browser has been through the flow before.
              repeat_claim: Boolean(repeat),
              previous_claim_at: repeat || '',
            },
            metric: {
              data: {
                type: 'metric',
                attributes: { name: this.dataset.metric || 'Prize Claimed' },
              },
            },
            profile: {
              data: { type: 'profile', attributes: profileAttributes },
            },
          },
        },
      });
      if (!response.ok) throw new Error('Klaviyo event rejected');

      if (data.get('subscribe') && this.dataset.listId) {
        // Consent is a bonus. A subscription hiccup must never read as a
        // failed claim, but leave a trace for debugging.
        try {
          const subscription = await this.post('subscriptions', {
            data: {
              type: 'subscription',
              attributes: {
                custom_source: this.dataset.metric || 'Prize Claimed',
                profile: {
                  data: {
                    type: 'profile',
                    attributes: Object.assign({}, profileAttributes, {
                      properties: { prize_channel: this.channel || '' },
                      subscriptions: {
                        email: { marketing: { consent: 'SUBSCRIBED' } },
                      },
                    }),
                  },
                },
              },
              relationships: {
                list: { data: { type: 'list', id: this.dataset.listId } },
              },
            },
          });
          if (!subscription.ok) {
            console.warn('Wild Gainz: Klaviyo list subscription failed', subscription.status);
          }
        } catch (subscribeError) {
          console.warn('Wild Gainz: Klaviyo list subscription failed', subscribeError);
        }
      }

      writeStore(CLAIM_KEY, new Date().toISOString());
      this.claimed = true;
      this.advance(reviewPanel);
    } catch (error) {
      this.busy = false;
      this.say(this.dataset.errorText, true);
    }
  }

  /* The claim is done, so the form is behind them: the history is dropped
     rather than kept, which hides the back link on the thank-you instead of
     offering a return to a form that has already been submitted. */
  advance(reviewPanel) {
    const target = reviewPanel || this.reviewPanel;
    this.busy = false;
    this.say('');
    this.history = [];
    if (target && this.panel(target)) {
      this.show(target, { track: false });
    }
  }

  say(text, isError) {
    const status = this.statusEl;
    if (!status) return;
    status.textContent = text || '';
    status.classList.toggle('wg-prize__status--error', Boolean(isError));
  }
}

customElements.define('wildgainz-prize-flow', WildgainzPrizeFlow);
})();
